import { InjectedGianoProvider, InjectedGianoProviderOptions } from '@/lib/giano'
import {
  createGianoProvider,
  createGianoConnector,
  CreateGianoProviderParams,
} from '@appliedblockchain/giano-connector'
import { Chain } from '@portafino/shared'
import { Address, parseGwei, Transport, Chain as ViemChain } from 'viem'
import { createBundlerClient } from 'viem/account-abstraction'
import { createConfig, custom, http } from 'wagmi'
import { base, baseSepolia, hardhat } from 'wagmi/chains'
import { env } from './env'

const rpcUrl = env.rpcUrl
const bundlerUrl = env.bundlerUrl

const rpcTransport = http(rpcUrl)
interface DynamicTransport extends Transport {
  updateHeaders: (headers: Record<string, string>) => void
  refreshHeaders: () => void
}

const createDynamicBundlerTransport = (url: string): DynamicTransport => {
  const currentHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const updateHeaders = (newHeaders: Record<string, string>) => {
    Object.assign(currentHeaders, newHeaders)
  }

  const refreshHeaders = () => {
    delete currentHeaders.Authorization
  }

  const transport = http(url, {
    fetchOptions: {
      headers: currentHeaders,
    },
  }) as DynamicTransport

  transport.updateHeaders = updateHeaders
  transport.refreshHeaders = refreshHeaders

  return transport
}

const bundlerTransport = createDynamicBundlerTransport(bundlerUrl)

export const updateBundlerAuth = (token: string | null) => {
  if (token) {
    bundlerTransport.updateHeaders({
      Authorization: `Bearer ${token}`,
    })
  } else {
    bundlerTransport.refreshHeaders()
  }
}

export const refreshBundlerAuth = () => {
  bundlerTransport.refreshHeaders()
}

const chains: Record<
  Chain,
  {
    id: number
    chain: ViemChain
  }
> = {
  [Chain.HARDHAT]: {
    id: hardhat.id,
    chain: hardhat,
  },
  [Chain.BASE]: {
    id: base.id,
    chain: base,
  },
  [Chain.BASE_SEPOLIA]: {
    id: baseSepolia.id,
    chain: baseSepolia,
  },
}

const { gianoPaymasterAddress, gianoSmartWalletFactoryAddress, chain } = env
const paymaster: Address | undefined = gianoPaymasterAddress
const chainConfig = chains[chain]

if (!chainConfig) {
  throw new Error(`Chain ${chain} not supported`)
}

export const bundler = createBundlerClient({
  chain: chainConfig.chain,
  transport: bundlerTransport,
  ...(chain === Chain.HARDHAT
    ? {
        paymaster: {
          //@ts-expect-error - the "required" fields are not needed to fulfill a user op
          getPaymasterData: async (): Promise<GetPaymasterDataReturnType> => ({
            paymaster,
          }),
          //@ts-expect-error - the "required" fields are not needed to fulfill a user op
          getPaymasterStubData: async (): Promise<GetPaymasterStubDataReturnType> => ({
            paymaster,
          }),
        },
        userOperation: {
          estimateFeesPerGas: async () => {
            return {
              maxFeePerGas: parseGwei('400'),
              maxPriorityFeePerGas: parseGwei('600'),
            }
          },
        },
      }
    : {
        paymaster: true,
      }),
})

export const getProvider = (providerOptions: InjectedGianoProviderOptions = {}) => {
  const { gianoProvider: provider } = createGianoProvider({
    bundler: bundler,
    paymaster,
    chains: [chainConfig.chain],
    transports: {
      [chainConfig.id]: rpcTransport,
    },
    initialChainId: chainConfig.id,
    injection: new InjectedGianoProvider(providerOptions),
    gianoSmartWalletFactoryAddress,
  } as CreateGianoProviderParams)

  return provider
}

export const provider = getProvider()

export const wagmiConfig = createConfig({
  chains: [hardhat],
  transports: {
    [hardhat.id]: custom(provider),
  },
  ssr: true,
})

export const getGianoConnector = (providerOptions: InjectedGianoProviderOptions) => {
  const provider = getProvider(providerOptions)
  return createGianoConnector({ provider })
}
